import { useState } from "preact/hooks";
import { api } from "../api/client.ts";
import type { CommentOut, Discussion as DiscussionOut, SpaceOut, Timeline } from "../api/types.gen.ts";
import { t } from "../i18n/index.ts";
import type { DiscussionCtx } from "../lib/conversation.ts";
import { conversationPath, speakersOf, subject } from "../lib/conversation.ts";
import { folderHref } from "../lib/folders.ts";
import { formatRelative } from "../lib/format.ts";
import { summary } from "../lib/history.ts";
import { spaceApi, spaceUrl } from "../lib/paths.ts";
import { byPlace, seriesOf } from "../lib/reports.ts";
import { bump, type Resource, useAction, useResource } from "../lib/resource.ts";
import { canEdit } from "../lib/roles.ts";
import { location } from "../lib/router.ts";
import { me, type PageProps } from "../lib/state.ts";
import { Avatar } from "../ui/Avatar.tsx";
import { BookmarkButton } from "../ui/BookmarkButton.tsx";
import { Button, Link } from "../ui/Button.tsx";
import { IssueBadges, SeverityLabel } from "../ui/counts.tsx";
import { EntityGlyph } from "../ui/EntityGlyph.tsx";
import { FolderIcon, MarkReadIcon } from "../ui/icons.tsx";
import { Async, Badge, EmptyState } from "../ui/misc.tsx";
import { PageHeader } from "../ui/PageHeader.tsx";
import { LINK } from "../ui/styles.ts";
import { TabNav } from "../ui/TabNav.tsx";
import { Composer } from "./comments/composer.tsx";
import { Post } from "./comments/post.tsx";
import { SeriesView } from "./comments/report.tsx";
import { RichText } from "./comments/richText.tsx";
import { WithEditors } from "./history/editors.tsx";
import { EntityLabel } from "./items/entityLabel.tsx";
import { PeekKindIcon } from "./items/icons.tsx";

const VIEWS = ["all", "mine", "unread"] as const;
type View = (typeof VIEWS)[number];

const viewOf = (): View => VIEWS.find((v) => v === location.value.searchParams.get("view")) ?? "all";

/** The item a discussion is about, as a compact card: its picture, kind, title, entity and issues. */
function ItemPreview({ space, d }: { space: SpaceOut; d: DiscussionOut }) {
  const row = d.item;
  if (!row) {
    return null;
  }
  const entity = space.entities.find((e) => e.id === row.entity);
  return (
    <span class="flex min-w-0 items-center gap-3">
      {row.thumb ? (
        // biome-ignore lint/correctness/useImageSize: user files have unknown dimensions; CSS bounds the box.
        <img
          src={row.thumb}
          alt=""
          loading="lazy"
          class="size-10 shrink-0 rounded border border-zinc-200 object-cover"
        />
      ) : null}
      <span class="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-0.5">
        <PeekKindIcon space={space} item={row} />
        <Link href={spaceUrl(space, `/items/${row.id}`)} class={`${LINK} min-w-0 truncate font-medium`}>
          {row.title || t("common.untitled")}
        </Link>
        {entity ? <EntityLabel entity={entity} /> : null}
        <IssueBadges counts={row.issues} />
      </span>
    </span>
  );
}

/** A link preview's frame: what the post is about, under its text. */
const EMBED = "flex flex-col gap-1.5 rounded-lg border border-zinc-200 bg-zinc-50 p-3 text-sm";

/** What a post is about, as a preview under it: the item (and its review or finding), the rule, the entity or field. */
function About({ space, d }: { space: SpaceOut; d: DiscussionOut }) {
  if (d.about.item) {
    return (
      <div class={EMBED}>
        <ItemPreview space={space} d={d} />
        {d.finding ? (
          <span class="flex items-start gap-2 text-zinc-700">
            <SeverityLabel severity={d.finding.severity} />
            <RichText space={space} text={d.finding.message} inline={true} class="line-clamp-2" />
          </span>
        ) : d.about.review ? (
          <Link href={spaceUrl(space, `/reviews/${d.about.review}`)} class={`${LINK} text-xs`}>
            {t("timeline.review", { name: d.reviewer_name ?? t("comments.someone") })}
          </Link>
        ) : null}
      </div>
    );
  }
  if (d.rule) {
    return (
      <div class={EMBED}>
        <span class="flex items-center gap-2">
          <Badge tone="gray">{t("timeline.rule")}</Badge>
          <Link href={spaceUrl(space, `/rules/${d.rule.id}`)} class={`${LINK} font-medium`}>
            {d.rule.name}
          </Link>
        </span>
        {d.rule.instructions ? <span class="line-clamp-2 text-zinc-600">{d.rule.instructions}</span> : null}
      </div>
    );
  }
  if (d.entity) {
    return (
      <div class={`${EMBED} flex-row items-center`}>
        <EntityGlyph icon={d.entity.icon} size={18} empty="dot" />
        <Link href={spaceUrl(space, `/entities/${d.entity.id}`)} class={`${LINK} font-medium`}>
          {d.entity.name}
        </Link>
        {d.entity.field_label ? (
          <span class="text-zinc-600">{t("timeline.field", { field: d.entity.field_label })}</span>
        ) : null}
      </div>
    );
  }
  if (d.commit) {
    // A change in the history, as its History line says it: what, who, when.
    return (
      <div class={EMBED}>
        <span class="flex items-center gap-2">
          <Badge tone="gray">{t("timeline.change")}</Badge>
          <Link href={spaceUrl(space, `/history/${d.commit.id}`)} class={`${LINK} min-w-0 truncate font-medium`}>
            {summary(d.commit, d.commit.changes)}
          </Link>
        </span>
        <span class="flex gap-2 text-xs text-zinc-500">
          <span>{d.commit.author_name ?? t("history.system")}</span>
          <WithEditors c={d.commit} />
          <span>{formatRelative(d.commit.created_at)}</span>
        </span>
      </div>
    );
  }
  if (d.about.folder) {
    return (
      <div class={`${EMBED} flex-row items-center`}>
        <FolderIcon size={16} class="text-zinc-500" />
        <Link href={folderHref(space, d.about.folder)} class={`${LINK} font-medium`}>
          {t("folders.aFolder")}
        </Link>
      </div>
    );
  }
  // A post for everyone: about nothing else.
  return null;
}

/** One discussion of the timeline: what it's about, its first comment and latest replies, a box to reply. */
function TimelineEntry({ space, d, reload }: { space: SpaceOut; d: DiscussionOut; reload: () => void }) {
  const topic = {
    ...(d.about.item ? { item: d.about.item } : {}),
    ...(d.about.rule ? { rule: d.about.rule } : {}),
    ...(d.about.entity ? { entity: d.about.entity } : {}),
    ...(d.about.commit ? { commit: d.about.commit } : {}),
    ...(d.about.folder ? { folder: d.about.folder } : {}),
  };
  const at = {
    ...(d.about.review ? { review: d.about.review } : {}),
    ...(d.about.finding ? { finding: d.about.finding } : {}),
    ...(d.about.field ? { field: d.about.field } : {}),
  };
  const ctx: DiscussionCtx = {
    space,
    topic,
    reload,
    people: new Set(d.comments.flatMap((c) => (c.author ? [c.author] : []))),
    speakers: speakersOf(d.comments, []),
    seen: d.seen,
  };
  const [root, ...replies] = d.comments;
  if (!root) {
    return null;
  }
  // A first comment since deleted: its replies stand on their own.
  const orphan = root.id !== d.root;
  return (
    <Post
      ctx={ctx}
      at={at}
      root={root}
      replies={replies}
      attachment={
        d.about.item || d.about.rule || d.about.entity || d.about.commit ? <About space={space} d={d} /> : undefined
      }
      more={orphan ? undefined : { count: d.more, href: spaceUrl(space, conversationPath(d.about)) }}
    />
  );
}

/** A page of the timeline in `view`: the latest, or those before `before`. */
function useTimeline(space: SpaceOut, view: View, before?: string) {
  return useResource(`timeline:${space.id}:${view}:${before ?? ""}`, (signal) =>
    api.get<Timeline>(
      spaceApi(space, "/timeline"),
      { for_me: view === "mine" ? true : null, unread: view === "unread" ? true : null, before: before ?? null },
      signal,
    ),
  );
}

/** Older discussions, a page at a time. */
function Older({ space, view, before }: { space: SpaceOut; view: View; before: string }) {
  const [shown, setShown] = useState(false);
  if (!shown) {
    return (
      <div class="flex justify-center">
        <Button onClick={() => setShown(true)}>{t("timeline.loadMore")}</Button>
      </div>
    );
  }
  return <OlderPage space={space} view={view} before={before} />;
}

function OlderPage({ space, view, before }: { space: SpaceOut; view: View; before: string }) {
  return <Page space={space} view={view} r={useTimeline(space, view, before)} first={false} />;
}

/** A page of discussions (`first`: the latest, empty in its view's words), and the way to older ones. */
function Page({ space, view, r, first }: { space: SpaceOut; view: View; r: Resource<Timeline>; first: boolean }) {
  return (
    <Async r={r}>
      {(page) =>
        page.data.length === 0 && first ? (
          <EmptyState title={t(`timeline.empty.${view}`)}>{null}</EmptyState>
        ) : (
          <>
            <ul class="flex flex-col gap-4">
              {/* A series of reports (one label, one conversation) as one, its newest. */}
              {seriesOf(
                page.data.filter((d) => d.comments.length > 0),
                (d) => d.comments[0] as CommentOut,
                byPlace,
              ).map((s) => (
                <SeriesView
                  key={s.latest.root}
                  series={s}
                  rootOf={(d) => d.comments[0] as CommentOut}
                  draw={(d) => <TimelineEntry key={d.root} space={space} d={d} reload={r.reload} />}
                />
              ))}
            </ul>
            {page.next ? <Older space={space} view={view} before={page.next} /> : null}
          </>
        )
      }
    </Async>
  );
}

/**
 * The space's conversations, together: every discussion about its items, reviews, findings, rules and entities, and
 * the posts for everyone, the most recently active first; and a box to post something new for the whole space.
 */
export function SpaceConversation({ space }: PageProps) {
  const view = viewOf();
  const base = spaceUrl(space, "/conversation");
  const latest = useTimeline(space, view);
  const [markAll, marking] = useAction(async () => {
    await api.post(spaceApi(space, "/comments/seen-all"));
    bump(); // every page shown, and the counts beside everything
  });
  return (
    <>
      <PageHeader
        title={t("nav.conversation")}
        buttons={[
          {
            key: "seen",
            label: t("timeline.markAll"),
            icon: <MarkReadIcon />,
            symbol: "checkmark.message",
            busy: marking,
            onSelect: () => markAll(),
          },
        ]}
      >
        <BookmarkButton space={space} target={{ kind: "conversation" }} size="md" variant="secondary" />
      </PageHeader>
      <div class="flex max-w-3xl flex-col gap-6">
        {canEdit(space) ? (
          <div class="flex gap-3 rounded-xl border border-zinc-200 bg-white p-4">
            <Avatar id={me.value?.id} name={me.value?.name ?? ""} size={40} />
            <div class="min-w-0 flex-1">
              <Composer
                space={space}
                about={{ people: new Set() }}
                placeholder={t("timeline.newPost")}
                sendLabel={t("timeline.post")}
                send={async (body) => {
                  await api.post(spaceApi(space, "/comments"), { ...subject({}), body });
                  latest.reload();
                }}
              />
            </div>
          </div>
        ) : null}
        {/* The tabs and what they show, together (the tabs keep their own space below). */}
        <div>
          <TabNav
            label={t("timeline.views")}
            tabs={VIEWS.map((v) => ({
              href: v === "all" ? base : `${base}?view=${v}`,
              label: t(`timeline.view.${v}`),
              current: v === view,
            }))}
          />
          <Page key={view} space={space} view={view} r={latest} first={true} />
        </div>
      </div>
    </>
  );
}
